import React, { useState, useMemo } from "react";
import { ReactComponent as UpwardIcon } from "../../../../Assets/Icons/upwardBlueIcon.svg";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { separateDateTimeAndConvertToLocale } from "../../../../utils/utilFunctions";
import { ReactComponent as ValidIcon } from "../../../../Assets/Icons/validsvg.svg";
import { ReactComponent as RulesIcon } from "../../../../Assets/Icons/rules&regulationsvg.svg";
import { ReactComponent as InStoreIcon } from "../../../../Assets/Icons/instoresvg.svg";
import { ReactComponent as WebIcon } from "../../../../Assets/Icons/webIcon.svg";
import { ReactComponent as CategoryIcon } from "../../../../Assets/Icons/categoryIcon.svg"; // Mock or generic if needed, but using inline generic for now
import { ReactComponent as FoodIcon } from "../../../../Assets/Icons/FoodAndBeverage.svg";

const OfferDetails = ({ offerData }) => {
  const [showAllRules, setShowAllRules] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Combine thumbnail and preview images for the carousel
  const allImages = useMemo(() => {
    const images = [];
    if (offerData?.thumbnailImage) images.push(offerData.thumbnailImage);
    if (Array.isArray(offerData?.previewImages)) {
      offerData.previewImages.forEach(img => {
        if (img && img !== offerData.thumbnailImage) images.push(img);
      });
    }
    return images;
  }, [offerData?.thumbnailImage, offerData?.previewImages]);

  var settings = {
    dots: allImages.length > 1,
    arrows: false,
    infinite: true,
    autoplay: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: "cubic-bezier(0.16, 1, 0.3, 1)",
    autoplaySpeed: 4000,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
  };

  // Compute consistent discount label and type tag
  const discountText = (() => {
    if (offerData?.discountTitle) return offerData.discountTitle;
    switch (offerData?.offerDiscountType) {
      case "FREE_OFFER":
        return "Free Offer";
      case "DISCOUNTED_PERCENT":
        return `Discounted Offer ${offerData?.discountRange || 0}%`;
      case "FIXED_DOLLAR":
        return `$${offerData?.fixedDollarAmount || 0} Fixed Price`;
      case "BUY_ONE_GET_ONE_FREE":
        return "Buy 1 Get 1 Free";
      case "BUNDLE_OFFER":
        return "Bundle Offer";
      default:
        return offerData?.discountRange !== undefined && offerData?.discountRange !== null
          ? `Discounted Offer ${offerData?.discountRange}%`
          : null;
    }
  })();

  const offerTypeTag = offerData?.offerType === 'INSTORE_SWOODLE_OFFER'
    ? 'In Store Offer'
    : (offerData?.offerType === 'ONLINE_OFFER' ? 'Online Offer' : '');

  // Inline Discount Icon (Percent Circle)
  const DiscountIcon = () => (
    <svg className="w-3 h-3 text-white mr-1" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.2" fill="transparent" />
      <text x="12" y="16" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">%</text>
    </svg>
  );

  const getCategoryIcon = (name) => {
    if (!name) return <CategoryIcon className="w-3 h-3 mr-1 text-[#00ADEF]" />;
    const n = name.toLowerCase();
    if (n.includes("food") || n.includes("beverage") || n.includes("dining")) {
      return <FoodIcon className="w-3 h-3 mr-1 text-[#00ADEF]" />;
    }
    // Add other mappings if specific icons become available. 
    // Currently using generic CategoryIcon for Retail, Fashion, etc. as strictly no other specific icons found in Assets.
    return <CategoryIcon className="w-3 h-3 mr-1 text-[#00ADEF]" />;
  };

  return (
    <div className="redeem-offer-wrapper flex flex-col justify-start items-start w-full bg-[#F8F9FB] shadow-lg" style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.15)' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '95%', display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 auto', padding: '8px 0' }}>
        {/* Ticket-shaped image */}
        <div className="ticket-image-multi-cutout w-[95%] h-[125px] mb-8 mx-auto mt-1" style={{ background: '#000', position: 'relative' }}>
          {allImages.length > 1 ? (
            <Slider className="w-full h-full review-details-carousel" {...settings}>
              {allImages.map((url, idx) => {
                const isAbsolute = typeof url === 'string' && /^(https?:)?\/\//i.test(url);
                const resolved = isAbsolute ? url : `${process.env.REACT_APP_S3_URL || ''}${url || ''}`;
                return (
                  <div key={idx} className="w-full h-[125px] outline-none">
                    <img src={resolved} className="w-full h-full object-cover" style={{ background: 'transparent', borderRadius: 0 }} />
                  </div>
                );
              })}
            </Slider>
          ) : (
            (() => {
              const url = allImages?.[0] || offerData?.thumbnailImage;
              const isAbsolute = typeof url === 'string' && /^(https?:)?\/\//i.test(url);
              const resolved = isAbsolute ? url : `${process.env.REACT_APP_S3_URL || ''}${url || ''}`;
              return (
                <img src={resolved} className="w-full h-full object-cover" style={{ background: 'transparent', borderRadius: 0 }} />
              );
            })()
          )}
        </div>

        {/* Card Content */}
        <div className="flex flex-col items-start justify-start w-full px-4 pt-1 pb-2">
          {/* 1. Title */}
          <p className="font-poppins font-[700] text-[16px] text-[#222] mb-2">
            {offerData?.title}
          </p>

          {/* 2. Primary Tags (Solid Blue) */}
          <div className="flex flex-wrap gap-2 mb-3">
            {offerTypeTag && (
              <span className="bg-[#00ADEF] text-white text-[11px] font-[500] rounded-[4px] px-2 py-1 flex items-center">
                {/* Ensure Icon matches Left Preview (WebIcon) */}
                {offerData?.offerType === 'ONLINE_OFFER' ? <WebIcon className="w-3 h-3 text-white mr-1" fill="currentColor" /> : <InStoreIcon className="w-3 h-3 text-white mr-1" fill="currentColor" />}
                {offerTypeTag}
              </span>
            )}
            {discountText && (
              <span className="bg-[#00ADEF] text-white text-[11px] font-[500] rounded-[4px] px-2 py-1 flex items-center">
                {offerData?.offerDiscountType !== "FIXED_DOLLAR" && <DiscountIcon />}
                {discountText}
              </span>
            )}
          </div>

          {/* 3. Description */}
          <p className="font-poppins font-[400] text-[12px] text-[#767676] mb-3 break-words w-full" style={{ wordBreak: 'break-word' }}>
            {offerData?.description}
          </p>

          {/* 4. Secondary Tags (Light Blue - Categories) */}
          <div className="flex flex-wrap gap-2 mb-3">
            {offerData?.category?.title && (
              <span className="bg-[#E6F7FF] text-[#00ADEF] text-[11px] font-[500] rounded-[4px] px-2 py-1 flex items-center">
                {getCategoryIcon(offerData?.category?.title)}
                {offerData?.category?.title}
              </span>
            )}
            {Array.isArray(offerData?.subCategory) && offerData?.subCategory.map((s, idx) => (
              <span key={idx} className="bg-[#E6F7FF] text-[#00ADEF] text-[11px] font-[500] rounded-[4px] px-2 py-1 flex items-center">
                {s?.title}
              </span>
            ))}
          </div>

          {/* Validity */}
          <div className="flex items-center gap-2 mb-2">
            <ValidIcon className="w-[14px] h-[14px]" style={{ stroke: '#00ADEF' }} />
            <span className="text-[#303030] text-[12px] font-[500]">Valid until {separateDateTimeAndConvertToLocale(offerData?.endDate)?.localDate || 'June 17, 2025'}</span>
          </div>

          {/* Rules & Regulations */}
          <div className="flex items-start gap-2 mb-1">
            <RulesIcon className="w-[14px] h-[14px] mt-[1px]" style={{ stroke: '#00ADEF' }} />
            <span className="text-[#303030] text-[12px] font-[500]">Rules and regulations:</span>
          </div>
          <div className={`${offerData?.ruleAndRegulation?.length >= 3 ? 'rules-list-anim' : ''} ${showAllRules ? 'expanded' : ''} w-full`}>
            <ul className="list-disc pl-8 text-[#303030] text-[11px] mb-2 font-[400]">
              {offerData?.ruleAndRegulation?.length > 0 ? (
                (showAllRules
                  ? offerData?.ruleAndRegulation
                  : offerData?.ruleAndRegulation.slice(0, 2)
                ).map((rule, idx) => (
                  <li key={idx} className="mb-1">{rule?.title}</li>
                ))
              ) : (
                <>
                  <li className="mb-1">Valid only in NSW.</li>
                  <li className="mb-1">This offer may also be used when dining alone.</li>
                  <li className="mb-1">Valid for Dine-In.</li>
                </>
              )}
            </ul>
          </div>

          {/* View More */}
          {offerData?.ruleAndRegulation?.length >= 3 && (
            <div className="w-full flex justify-start">
              <button
                className="text-[#303030] text-[12px] font-[600] flex items-center gap-1 mb-4 bg-transparent border-none p-0 cursor-pointer hover:underline"
                onClick={() => setShowAllRules(!showAllRules)}
              >
                {showAllRules ? "View Less" : "View More"}
                <span className="text-[14px]">{showAllRules ? " " : " >"}</span>
              </button>
            </div>
          )}
        </div>

        {/* Redeem Button */}
        <div className="w-full px-4" style={{ marginBottom: 0 }}>
          <button className="w-full bg-[#00ADEF] text-white font-poppins font-[600] text-[14px] rounded-[8px] py-3 mt-0 mb-2 shadow-sm">Redeem</button>
        </div>
      </div>
    </div>
  );
};

export default OfferDetails;
